import { Popover, PopoverContent, PopoverTrigger } from '@chiroup/components/ui/popover';
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useState } from 'react';

type FilterField = 'viewCount' | 'saveCount' | 'dismissCount' | 'shareCount';
type FilterOperator = 'gte' | 'lte' | 'eq';

export type MetricFilter = {
  id: string;
  field: FilterField;
  operator: FilterOperator;
  value: number;
};

const FIELD_OPTIONS: { value: FilterField; label: string }[] = [
  { value: 'viewCount', label: 'Views' },
  { value: 'saveCount', label: 'Saves' },
  { value: 'dismissCount', label: 'Dismissed' },
  { value: 'shareCount', label: 'Shares' },
];

const OPERATOR_OPTIONS: { value: FilterOperator; label: string; symbol: string }[] = [
  { value: 'gte', label: 'Min', symbol: '>=' },
  { value: 'lte', label: 'Max', symbol: '<=' },
  { value: 'eq', label: 'Exact', symbol: '=' },
];

type Props = {
  filters: MetricFilter[];
  onChange: (filters: MetricFilter[]) => void;
  dateFrom?: string;
  dateTo?: string;
  onDateChange: (dateFrom?: string, dateTo?: string) => void;
};

const DropdownSelect: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  className?: string;
}> = ({ value, options, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedLabel = options.find(o => o.value === value)?.label || '';

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center justify-between gap-1 h-8 px-2.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${className}`}
        >
          {selectedLabel}
          <ChevronDownIcon className="w-3 h-3 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-32 p-1">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => {
              onChange(opt.value);
              setIsOpen(false);
            }}
            className={`flex w-full items-center rounded-md px-2.5 py-1.5 text-sm transition-colors ${
              value === opt.value
                ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20 dark:text-primary-400 font-medium'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const ResearchAnalyticsFilters: React.FC<Props> = ({
  filters,
  onChange,
  dateFrom,
  dateTo,
  onDateChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newField, setNewField] = useState<FilterField>('viewCount');
  const [newOperator, setNewOperator] = useState<FilterOperator>('gte');
  const [newValue, setNewValue] = useState('');

  const addFilter = useCallback(() => {
    const val = Number(newValue);
    if (!Number.isFinite(val) || val < 0) return;

    const filter: MetricFilter = {
      id: `${Date.now()}`,
      field: newField,
      operator: newOperator,
      value: val,
    };

    onChange([...filters, filter]);
    setNewValue('');
  }, [filters, newField, newOperator, newValue, onChange]);

  const removeFilter = useCallback(
    (id: string) => {
      onChange(filters.filter(f => f.id !== id));
    },
    [filters, onChange]
  );

  const getFieldLabel = (field: FilterField) =>
    FIELD_OPTIONS.find(f => f.value === field)?.label || field;

  const getOperatorSymbol = (op: FilterOperator) =>
    OPERATOR_OPTIONS.find(o => o.value === op)?.symbol || op;

  const activeCount = filters.length + (dateFrom || dateTo ? 1 : 0);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Active filter pills */}
      {(dateFrom || dateTo) && (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 border border-primary-200 dark:border-primary-800">
          Date: {dateFrom || '...'} — {dateTo || '...'}
          <button
            onClick={() => onDateChange(undefined, undefined)}
            className="ml-0.5 hover:text-primary-900 dark:hover:text-primary-100"
          >
            <XMarkIcon className="w-3 h-3" />
          </button>
        </span>
      )}

      {filters.map(filter => (
        <span
          key={filter.id}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
        >
          {getFieldLabel(filter.field)} {getOperatorSymbol(filter.operator)} {filter.value}
          <button
            onClick={() => removeFilter(filter.id)}
            className="ml-0.5 hover:text-primary-900 dark:hover:text-primary-100"
          >
            <XMarkIcon className="w-3 h-3" />
          </button>
        </span>
      ))}

      {/* Add metric filter */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            {activeCount > 0 ? (
              <>
                <FunnelIcon className="w-3 h-3" />
                Add filter
              </>
            ) : (
              <>
                <PlusIcon className="w-3 h-3" />
                Filter
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-3">
            Add a metric filter
          </p>
          <div className="flex items-center gap-2">
            <DropdownSelect
              value={newField}
              options={FIELD_OPTIONS}
              onChange={v => setNewField(v as FilterField)}
              className="min-w-[100px]"
            />
            <DropdownSelect
              value={newOperator}
              options={OPERATOR_OPTIONS}
              onChange={v => setNewOperator(v as FilterOperator)}
              className="min-w-[70px]"
            />
            <input
              type="number"
              min="0"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') addFilter();
              }}
              placeholder="0"
              className="w-16 h-8 px-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-center"
            />
            <button
              onClick={addFilter}
              disabled={!newValue}
              className="h-8 px-3 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Date range filter — separate button */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            <CalendarDaysIcon className="w-3 h-3" />
            {dateFrom || dateTo ? 'Date range' : 'Date'}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Filter by publish date
          </p>
          <div className="flex items-center gap-2">
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">From</label>
              <input
                type="date"
                value={dateFrom || ''}
                onChange={e => onDateChange(e.target.value || undefined, dateTo)}
                className="h-8 px-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">To</label>
              <input
                type="date"
                value={dateTo || ''}
                onChange={e => onDateChange(dateFrom, e.target.value || undefined)}
                className="h-8 px-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ResearchAnalyticsFilters;
